/**
 * Sign selector — step 7.
 *
 * Four chip rows that DRIVE the theme's existing variant picker. The picker
 * stays the single source of truth for the variant id: chips set a <select> and
 * dispatch `change`, the picker's own listener resolves the variant and writes
 * its hidden `name="id"` input, and this script then reads the result back. It
 * never computes a variant id and never adds a third `name="id"` input
 * (DUAL-VARIANT-ID-INPUTS).
 *
 * It writes NO line-item properties. The Production row is display only —
 * rush is a shipping method chosen at checkout, and `Production` is not one of
 * the nine keys in PROPERTY-VOCABULARY.
 *
 * Degrades to today's page: if window.SignCore is missing or the wrong version,
 * the rows stay hidden and the native selects stay visible. Never to a broken
 * page.
 *
 * ONE-FACE SPECS (7.3, Session I-3). A spec with `printedFaces: 1` — the
 * fold-outs — has no Sides axis: its product carries Quantity and Stakes only,
 * the Sides row is not rendered, and `sides` is the constant 'single' in every
 * state and every `sign:selection` event, so sign-uploader.js never offers a
 * back. The spec is found through `data-product-id` and window.SignProducts
 * (sign-products.js). A product with no spec row — every sticker — behaves
 * exactly as before.
 */
(function () {
  'use strict';

  /* PUSH-SUCCESS-NOT-LIVE marker (item 38). Bump when this file changes. */
  var SIGN_SELECTOR_VERSION = '7.3';

  /**
   * MAJOR VERSION ONLY.
   *
   * This was an exact '6.0' match, and step 8b's bundle bump to 6.1 — which
   * only ADDED exports — silently degraded the whole selector to the native
   * picker on the live page. The chip rows vanished and nothing failed loudly:
   * the fallback is by design indistinguishable from "no selector was ever
   * built".
   *
   * A minor bump means the surface grew. Pin the major, which is what actually
   * changes when a name this file reads is removed or reshaped.
   */
  var REQUIRED_SIGN_CORE_MAJOR = '6.';

  var NATIVE_HIDDEN_CLASS = 'sign-selector-native-hidden';

  /* Shopify option values -> the unions sign-core speaks. The adapter lives
     here, not in sign-core: these strings are this store's option values. */
  var SIDES_TO_CORE = { 'Single Sided': 'single', 'Double Sided': 'double' };
  var STAKES_TO_CORE = { 'With Stakes': 'with', 'No Stakes': 'without' };
  var TIER_LABELS = { standard: 'Standard', rush: 'Rush', nextDay: 'Next-day' };

  function money(dollars) {
    return '$' + dollars.toFixed(2);
  }

  function optionPositions(pickerEl) {
    /* Resolve by NAME, not by hardcoded 1/2/3, so a reordered option list does
       not silently drive the wrong select. An option the product does not have
       resolves to null — a one-face product has no Sides — and only an
       unreadable options list falls back to the 1/2/3 defaults. */
    var defaults = { Quantity: 1, Sides: 2, Stakes: 3 };
    var node = pickerEl.querySelector('[data-product-options-json]');
    if (!node) return defaults;
    try {
      var positions = { Quantity: null, Sides: null, Stakes: null };
      JSON.parse(node.textContent).forEach(function (o) {
        if (Object.prototype.hasOwnProperty.call(positions, o.name)) positions[o.name] = o.position;
      });
      return positions;
    } catch (e) {
      console.warn('[sign-selector] could not parse product options JSON; using default positions', e);
      return defaults;
    }
  }

  /** The spec row for this product, or null for anything that is not a sign. */
  function specFor(root) {
    var map = window.SignProducts && window.SignProducts.SPEC_BY_PRODUCT_ID;
    var specId = map && map[root.getAttribute('data-product-id')];
    return (specId && window.SignCore.SIGN_SPECS[specId]) || null;
  }

  /** One printed face, always. A bundle older than 6.3 has no field: two. */
  function isOneFace(spec) {
    return !!spec && spec.printedFaces === 1;
  }

  function selectByPosition(pickerEl, position) {
    return pickerEl.querySelector('.variant-option-select[data-option-position="' + position + '"]');
  }

  function setNativeHidden(pickerEl, hidden) {
    var wrappers = pickerEl.querySelectorAll('.product-form__input--dropdown');
    for (var i = 0; i < wrappers.length; i++) {
      wrappers[i].classList.toggle(NATIVE_HIDDEN_CLASS, hidden);
    }
  }

  function build(root) {
    var sectionId = root.getAttribute('data-section-id');
    var picker = document.querySelector('.product-variant-picker[data-section-id="' + sectionId + '"]');
    if (!picker) {
      console.warn('[sign-selector] no variant picker for section', sectionId);
      return null;
    }

    var variants;
    try {
      variants = JSON.parse(picker.querySelector('[data-variants-json]').textContent);
    } catch (e) {
      console.warn('[sign-selector] could not parse variants JSON', e);
      return null;
    }

    var oneFace = isOneFace(specFor(root));
    var pos = optionPositions(picker);
    var selects = {
      quantity: pos.Quantity ? selectByPosition(picker, pos.Quantity) : null,
      sides: pos.Sides ? selectByPosition(picker, pos.Sides) : null,
      stakes: pos.Stakes ? selectByPosition(picker, pos.Stakes) : null
    };
    if (!selects.quantity || !selects.stakes || (!oneFace && !selects.sides)) {
      console.warn('[sign-selector] expected ' + (oneFace ? 'Quantity and Stakes' : 'three option') +
        ' selects, found', selects);
      return null;
    }
    /* A one-face spec on a product that still carries a Sides option is an
       Admin misconfiguration (the product should have no Sides). Pin the select
       to Single Sided rather than break the page, so only one-face variants can
       ever be selected; the app's parser refuses such a product outright. */
    if (oneFace && selects.sides) {
      console.warn('[sign-selector] one-face spec on a product with a Sides option — pinned to Single Sided');
      if (selects.sides.value !== 'Single Sided') {
        selects.sides.value = 'Single Sided';
        selects.sides.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* The picker's own id input — NOT buy-buttons' second one. */
    var idInput = picker.querySelector('.product-variant-id');
    if (!idInput) {
      console.warn('[sign-selector] variant picker has no id input');
      return null;
    }

    var rows = [];
    var availability = {};
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var sidesValue = pos.Sides ? v['option' + pos.Sides] : null;
      /* One face: every row is 'single', and a Double Sided row (only possible
         on a misconfigured product) is skipped rather than read. */
      var sides = oneFace
        ? (sidesValue === null || sidesValue === 'Single Sided' ? 'single' : null)
        : SIDES_TO_CORE[sidesValue];
      var stakes = STAKES_TO_CORE[v['option' + pos.Stakes]];
      var qty = parseInt(v['option' + pos.Quantity], 10);
      if (!sides || !stakes || !isFinite(qty)) {
        console.warn('[sign-selector] skipping variant with unmapped options', v.title);
        continue;
      }
      rows.push({
        quantity: qty,
        sides: sides,
        stakes: stakes,
        packPrice: v.price / 100,
        variantId: String(v.id)
      });
      /* Keyed off this row, not a parallel index — `rows` skips unmapped
         variants, so indices would drift the moment one is skipped. */
      availability[qty + '|' + sides + '|' + stakes] = v.available !== false;
    }

    /* The Quantity row's chips come from SIGN_QUANTITIES; assert it still
       matches what the product actually sells. Warn, never break the page. */
    var productQuantities = [];
    for (var q = 0; q < selects.quantity.options.length; q++) {
      productQuantities.push(parseInt(selects.quantity.options[q].value, 10));
    }
    var coreQuantities = Array.prototype.slice.call(window.SignCore.SIGN_QUANTITIES);
    if (productQuantities.join(',') !== coreQuantities.join(',')) {
      console.warn(
        '[sign-selector] SIGN_QUANTITIES does not match the product option values.',
        'core:', coreQuantities, 'product:', productQuantities
      );
    }

    return {
      root: root,
      picker: picker,
      oneFace: oneFace,
      selects: selects,
      idInput: idInput,
      variants: variants,
      priceTable: window.SignCore.buildPriceTable(rows),
      availability: availability
    };
  }

  function currentState(ctx) {
    return {
      quantity: parseInt(ctx.selects.quantity.value, 10),
      sides: ctx.oneFace ? 'single' : SIDES_TO_CORE[ctx.selects.sides.value],
      stakes: STAKES_TO_CORE[ctx.selects.stakes.value]
    };
  }

  function chip(label, sub, pressed, disabled, onClick, title) {
    var b = document.createElement('button');
    b.type = 'button';                       /* never submits the form */
    b.className = 'sign-selector__chip';
    b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    if (disabled) b.disabled = true;
    if (title) b.title = title;
    var v = document.createElement('span');
    v.className = 'sign-selector__chip-value';
    v.textContent = label;
    b.appendChild(v);
    if (sub) {
      var s = document.createElement('span');
      s.className = 'sign-selector__chip-unit';
      s.textContent = sub;
      b.appendChild(s);
    }
    b.addEventListener('click', onClick);
    return b;
  }

  /* Set a select and let the PICKER resolve the variant. */
  function choose(ctx, key, value) {
    var select = ctx.selects[key];
    if (select.value === value) return;
    select.value = value;
    /* Synchronous: the picker's listener writes the id input and ours
       re-renders, both before this returns. Do not also call render() here. */
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function lookup(ctx, quantity, sides, stakes) {
    return ctx.priceTable.get(window.SignCore.variantKey(quantity, sides, stakes)) || null;
  }

  function available(ctx, quantity, sides, stakes) {
    var k = quantity + '|' + sides + '|' + stakes;
    return ctx.availability[k] !== false;
  }

  function render(ctx) {
    var SC = window.SignCore;
    var state = currentState(ctx);
    var orderedAt = new Date();     /* recomputed every interaction, not cached */

    /* ---- Quantity ---- */
    var qBox = ctx.root.querySelector('[data-chips="quantity"]');
    qBox.textContent = '';
    Array.prototype.slice.call(ctx.selects.quantity.options).forEach(function (opt) {
      var qty = parseInt(opt.value, 10);
      var row = lookup(ctx, qty, state.sides, state.stakes);
      var sub = row ? money(row.packPrice / qty) + ' ea' : '';
      var ok = available(ctx, qty, state.sides, state.stakes);
      qBox.appendChild(chip(
        String(qty), sub, qty === state.quantity, !ok,
        function () { choose(ctx, 'quantity', opt.value); },
        ok ? null : 'Currently unavailable'
      ));
    });

    /* ---- Sides / Stakes ---- */
    /* One face: no Sides row at all — not disabled chips, not a lone "Single
       Sided" chip. The row is hidden and never filled. */
    var sidesRow = ctx.root.querySelector('[data-row="sides"]');
    if (sidesRow) sidesRow.hidden = ctx.oneFace;
    var rowKeys = ctx.oneFace
      ? [['stakes', STAKES_TO_CORE]]
      : [['sides', SIDES_TO_CORE], ['stakes', STAKES_TO_CORE]];
    rowKeys.forEach(function (pair) {
      var key = pair[0], map = pair[1];
      var box = ctx.root.querySelector('[data-chips="' + key + '"]');
      box.textContent = '';
      Array.prototype.slice.call(ctx.selects[key].options).forEach(function (opt) {
        var coreValue = map[opt.value];
        var probe = {
          quantity: state.quantity,
          sides: key === 'sides' ? coreValue : state.sides,
          stakes: key === 'stakes' ? coreValue : state.stakes
        };
        var ok = available(ctx, probe.quantity, probe.sides, probe.stakes);
        box.appendChild(chip(
          opt.value, null, coreValue === state[key], !ok,
          function () { choose(ctx, key, opt.value); },
          ok ? null : 'Currently unavailable'
        ));
      });
    });

    /* ---- Production: display only ---- */
    var tierBox = ctx.root.querySelector('[data-tiers]');
    var totalEl = ctx.root.querySelector('[data-total]');
    tierBox.textContent = '';

    var resolved = null;
    try {
      resolved = SC.resolveSelection(
        { quantity: state.quantity, sides: state.sides, stakes: state.stakes, tier: 'standard' },
        ctx.priceTable,
        orderedAt
      );
    } catch (e) {
      console.warn('[sign-selector] could not resolve selection', e);
    }

    if (resolved) {
      resolved.tierOptions.forEach(function (t) {
        var d = document.createElement('div');
        d.className = 'sign-selector__tier';
        d.setAttribute('role', 'listitem');
        d.setAttribute('data-tier', t.tier);
        var n = document.createElement('span');
        n.className = 'sign-selector__tier-name';
        n.textContent = TIER_LABELS[t.tier] || t.tier;
        var dt = document.createElement('span');
        dt.className = 'sign-selector__tier-date';
        dt.textContent = 'Ships ' + t.shipDateLabel;
        var f = document.createElement('span');
        f.className = 'sign-selector__tier-fee';
        f.textContent = t.feeLabel;
        d.appendChild(n); d.appendChild(dt); d.appendChild(f);
        tierBox.appendChild(d);
      });

      totalEl.textContent =
        state.quantity + (state.quantity === 1 ? ' sign' : ' signs') +
        ' × ' + money(resolved.unitPrice) +
        ' = ' + money(resolved.packPrice);
    } else {
      totalEl.textContent = '';
    }

    announce(state, resolved);
  }

  /**
   * Tell the rest of the page what is selected.
   *
   * sign-uploader.js needs Sides to know whether a Proof is one face or two, and
   * it has no way to get it: Sides is a variant option, its position is resolved
   * BY NAME here (`optionPositions`) precisely because hardcoding 1/2/3 silently
   * drives the wrong select, and a second copy of that resolution in the
   * uploader would be that same bug waiting in a second file. So this file
   * dispatches and that one listens.
   *
   * LATCHED AS WELL AS DISPATCHED. Both scripts are deferred and run in document
   * order, so whichever is second misses the other's first event; a listener
   * added after this fires would sit on the default until the customer happened
   * to touch a chip. `selection` is the current answer for anyone who arrives
   * late, and the event is the change.
   *
   * Still NO line-item property: this is display and coordination. `resolved`
   * may be null — a selection sign-core could not price is still a selection,
   * and a listener that only wants `sides` should not go dark because the price
   * table missed.
   */
  function announce(state, resolved) {
    var detail = {
      quantity: state.quantity,
      sides: state.sides,
      stakes: state.stakes,
      unitPrice: resolved ? resolved.unitPrice : null,
      packPrice: resolved ? resolved.packPrice : null
    };
    window.SignSelector.selection = detail;
    document.dispatchEvent(new CustomEvent('sign:selection', {
      bubbles: true,
      detail: detail
    }));
  }

  function degrade(root) {
    if (root) root.hidden = true;
    var picker = root && document.querySelector(
      '.product-variant-picker[data-section-id="' + root.getAttribute('data-section-id') + '"]'
    );
    if (picker) setNativeHidden(picker, false);
  }

  function init() {
    var root = document.querySelector('[data-sign-selector]');
    if (!root) return;

    var coreVersion = window.SignCore && window.SignCore.SIGN_CORE_VERSION;
    if (typeof coreVersion !== 'string' || coreVersion.indexOf(REQUIRED_SIGN_CORE_MAJOR) !== 0) {
      console.warn(
        '[sign-selector] SignCore ' + REQUIRED_SIGN_CORE_MAJOR + 'x required, got ' +
        (coreVersion || 'nothing') + '; leaving the native variant picker in place.'
      );
      degrade(root);
      return;
    }

    var ctx = build(root);
    if (!ctx) { degrade(root); return; }

    setNativeHidden(ctx.picker, true);
    root.hidden = false;
    render(ctx);

    /* If anything else changes a select (back button, another script), follow it. */
    Object.keys(ctx.selects).forEach(function (k) {
      if (ctx.selects[k]) ctx.selects[k].addEventListener('change', function () { render(ctx); });
    });
  }

  window.SignSelector = { SIGN_SELECTOR_VERSION: SIGN_SELECTOR_VERSION, init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
